/** Вкладка «Прогноз продаж» — порт renderPlanMatrix (build-скрипт, стр. 7236-7364).
 * Иерархия: артикул (read-only итог) → группа «Маркетплейсы» → редактируемые каналы МП
 * → редактируемые оптовые каналы (факт по опту отсутствует — «—»). Свой matrix-запрос:
 * плановый период шире основного, date_to кламплен к максимуму данных. Ячейка месяца
 * компактная: план (или инпут) + факт/Δ% — данные оригинала, вёрстка плотнее. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/orders";
import * as planningApi from "@/api/orders-planning";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { buildPlanCsv, downloadTextFile, parsePlanCsv, type PlanCsvRow } from "./csv";
import { int0, monthLabel, signed } from "./format";
import { matchesSearch } from "./metrics";
import {
  defaultPlanBounds,
  monthsBetween,
  planKey,
  planStatusClass,
  planValue,
  planValueTotal,
  salesPlanChannels,
  yearsOfMonths,
  type PlanChannelInfo,
  type PlanMap,
} from "./plans";
import { useOrdersEdit } from "./useOrdersEdit";

const STATUS_CLS: Record<string, string> = {
  "plan-ok": "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  "plan-warn": "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  "plan-bad": "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
};

type ChannelView = "all" | "total" | "mp" | "wholesale";

interface ArticleAgg {
  article: string;
  name: string;
  brand: string;
  code_1c: string;
  id_1c: string;
  qtySum: number;
  months: number[]; // факт всех МП по месяцам планового периода
  mpMonths: Map<string, number[]>; // mp_code → факт по месяцам
}

interface FlatRow {
  kind: "article" | "mp-group" | "channel";
  agg: ArticleAgg;
  channel?: PlanChannelInfo;
}

const ROW_H = 40;
const MONTH_W = 150;

export function PlanTab({ report }: { report: api.OrdersMatrixReport }): React.ReactElement {
  const qc = useQueryClient();
  const canEdit = useOrdersEdit();
  const bounds = defaultPlanBounds(
    report.meta.min_date ?? report.date_from,
    report.meta.max_date ?? report.date_to,
  );
  const [planFrom, setPlanFrom] = useState(bounds.from);
  const [planTo, setPlanTo] = useState(bounds.to);
  const [search, setSearch] = useState("");
  const [channelView, setChannelView] = useState<ChannelView>("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandedMp, setExpandedMp] = useState<Set<string>>(new Set());

  const months = useMemo(
    () => monthsBetween(planFrom.slice(0, 7), planTo.slice(0, 7)),
    [planFrom, planTo],
  );
  const years = useMemo(() => yearsOfMonths(months), [months]);
  const planYear = Number(planTo.slice(0, 4));

  // факт: свой matrix-запрос с клампом date_to к максимуму данных
  const factTo = report.meta.max_date && planTo > report.meta.max_date ? report.meta.max_date : planTo;
  const factQ = useQuery({
    queryKey: ["orders", "matrix", planFrom, factTo],
    queryFn: () => api.getMatrix({ date_from: planFrom, date_to: factTo }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const channelsQ = useQuery({
    queryKey: ["orders", "refs", "channels"],
    queryFn: planningApi.listChannels,
  });
  const channels = useMemo(() => salesPlanChannels(channelsQ.data ?? []), [channelsQ.data]);
  const mpChannels = channels.filter((c) => c.type === "mp");
  const wholesaleChannels = channels.filter((c) => c.type === "wholesale");

  const planQ = useQuery({
    queryKey: ["orders", "sales-plan", years],
    queryFn: async () => {
      const perYear = new Map<number, PlanMap>();
      for (const year of years) {
        const cells = await planningApi.listSalesPlan(year);
        const map: PlanMap = new Map();
        for (const c of cells) map.set(planKey(c.article, c.channel_code, c.month), c.qty);
        perYear.set(year, map);
      }
      return perYear;
    },
  });

  const saveCell = useMutation({
    mutationFn: planningApi.upsertSalesPlanCell,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", "sales-plan"] }),
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить план")),
  });
  const bulkMut = useMutation({
    mutationFn: (v: { year: number; entries: { article: string; channel_code: string; months: Record<number, number> }[] }) =>
      planningApi.bulkSalesPlan(v.year, v.entries),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["orders", "sales-plan"] });
      toast.success(`Импортировано значений: ${r.written}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Импорт плана не удался")),
  });
  const clearMut = useMutation({
    mutationFn: () => planningApi.clearSalesPlan(planYear),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", "sales-plan"] });
      toast.success(`План ${planYear} очищен`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось очистить план")),
  });

  // агрегация факта по артикулам (порт articleRowsForYear)
  const articles = useMemo<ArticleAgg[]>(() => {
    const map = new Map<string, ArticleAgg>();
    const factMonths = factQ.data?.months ?? [];
    const idx = new Map(factMonths.map((m, i) => [m, i]));
    for (const r of factQ.data?.rows ?? []) {
      let agg = map.get(r.article);
      if (!agg) {
        agg = {
          article: r.article, name: r.name ?? r.article, brand: r.brand ?? "",
          code_1c: r.code_1c ?? "", id_1c: r.id_1c ?? "",
          qtySum: 0,
          months: months.map(() => 0),
          mpMonths: new Map(),
        };
        map.set(r.article, agg);
      }
      let mp = agg.mpMonths.get(r.mp_code);
      if (!mp) {
        mp = months.map(() => 0);
        agg.mpMonths.set(r.mp_code, mp);
      }
      months.forEach((month, i) => {
        const fi = idx.get(month);
        if (fi === undefined) return;
        const q = r.qty[fi] ?? 0;
        agg!.months[i] = (agg!.months[i] ?? 0) + q;
        mp![i] = (mp![i] ?? 0) + q;
        agg!.qtySum += q;
      });
    }
    return [...map.values()]
      .filter((a) => matchesSearch(a, search))
      .sort((a, b) => b.qtySum - a.qtySum || a.article.localeCompare(b.article, "ru"));
  }, [factQ.data, months, search]);

  // селектор: "total" — все каналы, "mp" — только МП-каналы, иначе — конкретный код
  const planFor = (article: string, selector: string, monthIdx: number): number => {
    const month = months[monthIdx]!;
    const year = Number(month.slice(0, 4));
    const plan = planQ.data?.get(year);
    if (!plan) return 0;
    const m = Number(month.slice(5, 7));
    if (selector === "total") return planValueTotal(plan, channels, article, m);
    if (selector === "mp") return planValueTotal(plan, mpChannels, article, m);
    return planValue(plan, article, selector, m);
  };

  const showMp = channelView === "all" || channelView === "mp";
  const showWholesale = channelView === "all" || channelView === "wholesale";
  const showChildren = channelView !== "total";

  const flat = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    for (const agg of articles) {
      rows.push({ kind: "article", agg });
      if (!showChildren || !expanded.has(agg.article)) continue;
      if (showMp) {
        rows.push({ kind: "mp-group", agg });
        if (expandedMp.has(agg.article)) {
          for (const ch of mpChannels) rows.push({ kind: "channel", agg, channel: ch });
        }
      }
      if (showWholesale) {
        for (const ch of wholesaleChannels) rows.push({ kind: "channel", agg, channel: ch });
      }
    }
    return rows;
  }, [articles, expanded, expandedMp, showChildren, showMp, showWholesale, mpChannels, wholesaleChannels]);

  const gridTemplate = useMemo(
    () => `300px ${months.map(() => `${MONTH_W}px`).join(" ")} 110px 110px 110px 96px`,
    [months],
  );

  const parentRef = useRef<HTMLDivElement>(null);
  const virt = useVirtualizer({
    count: flat.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    overscan: 16,
  });

  // ── CSV ──
  const csvRows = (onlyFilled: boolean): PlanCsvRow[] => {
    const result: PlanCsvRow[] = [];
    const plan = planQ.data?.get(planYear) ?? new Map();
    const articleSet = new Set(articles.map((a) => a.article));
    for (const key of plan.keys()) articleSet.add(key.split("|")[0]!);
    const sorted = [...articleSet].sort((a, b) => a.localeCompare(b, "ru"));
    for (const article of sorted) {
      const name = articles.find((a) => a.article === article)?.name ?? article;
      for (const ch of channels) {
        const values = Array.from({ length: 12 }, (_, i) =>
          planValue(plan, article, ch.code, i + 1),
        );
        if (onlyFilled && !values.some((v) => v > 0)) continue;
        result.push({ article, name, channel_code: ch.code, channel_name: ch.label, values });
      }
    }
    return result;
  };
  const fileRef = useRef<HTMLInputElement>(null);

  if (factQ.isLoading || planQ.isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        <RefreshCw className="size-4 animate-spin" /> Загружаю план и факт планового периода…
      </div>
    );
  }
  if (factQ.isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
        Не удалось получить факт: {extractErrorMessage(factQ.error, "ошибка запроса")}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Плановый период:</span>
        <input
          type="date"
          value={planFrom}
          onChange={(e) => e.target.value && setPlanFrom(e.target.value)}
          className="h-8 rounded border bg-background px-2 text-xs"
        />
        <span className="text-muted-foreground">—</span>
        <input
          type="date"
          value={planTo}
          onChange={(e) => e.target.value && setPlanTo(e.target.value)}
          className="h-8 rounded border bg-background px-2 text-xs"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск"
          className="h-8 w-56 rounded border bg-background px-2 text-xs"
        />
        <select
          value={channelView}
          onChange={(e) => setChannelView(e.target.value as ChannelView)}
          className="h-8 rounded border bg-background px-2 text-xs"
        >
          <option value="all">Все каналы</option>
          <option value="total">Совокупно</option>
          <option value="mp">Только МП</option>
          <option value="wholesale">Только опт</option>
        </select>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadTextFile(`sales-plan-template-${planYear}.csv`, buildPlanCsv(planYear, csvRows(false), false))}
          >
            Шаблон
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const rows = csvRows(true);
              if (!rows.length) {
                toast.error("Нет сохранённого плана за год");
                return;
              }
              downloadTextFile(`sales-plan-saved-${planYear}.csv`, buildPlanCsv(planYear, rows, true));
            }}
          >
            Экспорт
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                Импорт
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  const entries = parsePlanCsv(
                    await file.text(),
                    new Set(channels.map((c) => c.code)),
                  );
                  if (!entries.length) {
                    toast.error("В файле нет строк плана");
                    return;
                  }
                  bulkMut.mutate({ year: planYear, entries });
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 dark:text-red-400"
                onClick={() => {
                  if (window.confirm(`Очистить весь план ${planYear} года?`)) clearMut.mutate();
                }}
              >
                Очистить план
              </Button>
            </>
          )}
        </div>
      </div>

      <div ref={parentRef} className="max-h-[calc(100vh-280px)] overflow-auto rounded-md border">
        <div className="min-w-max">
          {/* шапка */}
          <div className="sticky top-0 z-20 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
            <div className="grid text-[11px] font-medium text-muted-foreground" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="sticky left-0 z-10 bg-background px-2 py-1.5">Артикул / канал</div>
              {months.map((m) => (
                <div key={m} className="border-l px-2 py-1.5 text-right">
                  {monthLabel(m)}
                  <div className="text-[9px] font-normal">план · факт · Δ%</div>
                </div>
              ))}
              <div className="border-l px-2 py-1.5 text-right">План</div>
              <div className="border-l px-2 py-1.5 text-right">Факт</div>
              <div className="border-l px-2 py-1.5 text-right">Откл.</div>
              <div className="border-l px-2 py-1.5 text-right">Вып.</div>
            </div>
            {/* ИТОГО */}
            <TotalsRow articles={articles} months={months} planFor={planFor} gridTemplate={gridTemplate} />
          </div>

          <div style={{ height: virt.getTotalSize(), position: "relative" }}>
            {virt.getVirtualItems().map((vi) => {
              const row = flat[vi.index];
              if (!row) return null;
              return (
                <div
                  key={`${row.kind}:${row.agg.article}:${row.channel?.code ?? ""}`}
                  className="absolute left-0 w-full border-b"
                  style={{ top: vi.start, height: ROW_H }}
                >
                  <PlanRow
                    row={row}
                    months={months}
                    planFor={planFor}
                    canEdit={canEdit}
                    gridTemplate={gridTemplate}
                    expanded={expanded.has(row.agg.article)}
                    mpExpanded={expandedMp.has(row.agg.article)}
                    onToggle={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.agg.article)) next.delete(row.agg.article);
                        else next.add(row.agg.article);
                        return next;
                      })
                    }
                    onToggleMp={() =>
                      setExpandedMp((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.agg.article)) next.delete(row.agg.article);
                        else next.add(row.agg.article);
                        return next;
                      })
                    }
                    onSave={(channelCode, monthIdx, qty) => {
                      const month = months[monthIdx]!;
                      saveCell.mutate({
                        year: Number(month.slice(0, 4)),
                        article: row.agg.article,
                        channel_code: channelCode,
                        month: Number(month.slice(5, 7)),
                        qty,
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function TotalsRow({
  articles,
  months,
  planFor,
  gridTemplate,
}: {
  articles: ArticleAgg[];
  months: string[];
  planFor: (article: string, selector: string, monthIdx: number) => number;
  gridTemplate: string;
}): React.ReactElement {
  const totals = useMemo(() => {
    const plan = months.map((_, i) => articles.reduce((s, a) => s + planFor(a.article, "total", i), 0));
    const fact = months.map((_, i) => articles.reduce((s, a) => s + (a.months[i] ?? 0), 0));
    return { plan, fact, planSum: plan.reduce((s, v) => s + v, 0), factSum: fact.reduce((s, v) => s + v, 0) };
  }, [articles, months, planFor]);
  const pct = totals.planSum ? Math.round((totals.factSum / totals.planSum) * 100) : 0;
  return (
    <div className="grid border-t bg-muted/60 text-xs font-semibold" style={{ gridTemplateColumns: gridTemplate }}>
      <div className="sticky left-0 z-10 bg-muted px-2 py-1.5">ИТОГО</div>
      {months.map((m, i) => (
        <div key={m} className="border-l px-2 py-1.5 text-right tabular-nums">
          {totals.plan[i] ? int0(totals.plan[i]!) : "—"} · {totals.fact[i] ? int0(totals.fact[i]!) : "—"}
        </div>
      ))}
      <div className="border-l px-2 py-1.5 text-right tabular-nums">{int0(totals.planSum)}</div>
      <div className="border-l px-2 py-1.5 text-right tabular-nums">{int0(totals.factSum)}</div>
      <div className="border-l px-2 py-1.5 text-right tabular-nums">{signed(totals.factSum - totals.planSum)}</div>
      <div className="border-l px-2 py-1.5 text-right">
        {totals.planSum ? (
          <span className={cn("rounded px-1.5 py-0.5", STATUS_CLS[planStatusClass(pct, totals.planSum)])}>
            {pct}%
          </span>
        ) : (
          "—"
        )}
      </div>
    </div>
  );
}

function PlanRow({
  row,
  months,
  planFor,
  canEdit,
  gridTemplate,
  expanded,
  mpExpanded,
  onToggle,
  onToggleMp,
  onSave,
}: {
  row: FlatRow;
  months: string[];
  planFor: (article: string, selector: string, monthIdx: number) => number;
  canEdit: boolean;
  gridTemplate: string;
  expanded: boolean;
  mpExpanded: boolean;
  onToggle: () => void;
  onToggleMp: () => void;
  onSave: (channelCode: string, monthIdx: number, qty: number) => void;
}): React.ReactElement {
  const { agg, channel } = row;
  const isChannel = row.kind === "channel";
  const isMpGroup = row.kind === "mp-group";
  const selector = isChannel ? channel!.code : isMpGroup ? "mp" : "total";
  const editable = isChannel && canEdit;

  // факт: артикул — все МП; канал МП — mpMonths; опт/группа МП — соответствующее
  const factAt = (i: number): number | null => {
    if (row.kind === "article") return agg.months[i] ?? 0;
    if (isMpGroup) return agg.months[i] ?? 0;
    if (channel?.type === "mp") return agg.mpMonths.get(channel.code)?.[i] ?? 0;
    return null; // опт — факта нет
  };

  const planSum = months.reduce((s, _, i) => s + planFor(agg.article, selector, i), 0);
  const factSum = months.reduce((s, _, i) => s + (factAt(i) ?? 0), 0);
  const pct = planSum ? Math.round((factSum / planSum) * 100) : 0;

  return (
    <div
      // фон строки НЕПРОЗРАЧНЫЙ: липкая первая колонка берёт его (`bg-inherit`), под ней прокручиваются
      // месяцы; оттенки смешаны с фоном заранее (как `bg-muted/40`, `bg-amber-50/50` поверх фона)
      className={cn(
        "grid h-full items-stretch bg-background text-xs",
        isMpGroup && "bg-[color-mix(in_srgb,hsl(var(--muted))_40%,hsl(var(--background)))]",
        isChannel &&
          channel?.type === "wholesale" &&
          "bg-[color-mix(in_srgb,theme(colors.amber.50)_50%,hsl(var(--background)))] dark:bg-[color-mix(in_srgb,theme(colors.amber.950)_20%,hsl(var(--background)))]",
      )}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      <div className="sticky left-0 z-10 flex items-center gap-1 overflow-hidden bg-inherit px-2">
        {row.kind === "article" && (
          <button type="button" onClick={onToggle} className="shrink-0 text-muted-foreground hover:text-foreground">
            {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </button>
        )}
        {isMpGroup && (
          <button
            type="button"
            onClick={onToggleMp}
            className="ml-4 flex shrink-0 items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            {mpExpanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            Маркетплейсы
          </button>
        )}
        {isChannel && (
          <span className={cn("truncate", channel?.type === "mp" ? "ml-8" : "ml-4")}>
            {channel?.label}
          </span>
        )}
        {row.kind === "article" && (
          <div className="min-w-0">
            <div className="truncate font-medium">{agg.article}</div>
            <div className="truncate text-[10px] text-muted-foreground">{agg.name}</div>
          </div>
        )}
      </div>
      {months.map((m, i) => {
        const plan = planFor(agg.article, selector, i);
        const fact = factAt(i);
        return (
          <div key={m} className="flex items-center justify-end gap-1 border-l px-1.5 tabular-nums">
            {editable ? (
              <input
                key={`${agg.article}:${channel!.code}:${m}:${plan}`}
                type="number"
                min={0}
                defaultValue={plan || ""}
                className="h-6 w-14 rounded border bg-background px-1 text-right text-[11px] focus:border-primary focus:outline-none"
                onClick={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  const v = Math.max(0, Math.round(Number(e.target.value) || 0));
                  if (v !== plan) onSave(channel!.code, i, v);
                }}
              />
            ) : (
              <span className={cn(!plan && "text-muted-foreground")}>{plan ? int0(plan) : "—"}</span>
            )}
            <span className="text-[10px] text-muted-foreground">
              {fact === null ? "· —" : `· ${fact ? int0(fact) : "—"}`}
              {plan > 0 && fact !== null && (
                <span className={cn("ml-0.5", fact - plan >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400")}>
                  {Math.round((fact / plan) * 100)}%
                </span>
              )}
            </span>
          </div>
        );
      })}
      <div className="flex items-center justify-end border-l px-2 tabular-nums">{planSum ? int0(planSum) : "—"}</div>
      <div className="flex items-center justify-end border-l px-2 tabular-nums">
        {factAt(0) === null ? "—" : int0(factSum)}
      </div>
      <div className="flex items-center justify-end border-l px-2 tabular-nums">
        {planSum && factAt(0) !== null ? signed(factSum - planSum) : "—"}
      </div>
      <div className="flex items-center justify-end border-l px-2">
        {planSum && factAt(0) !== null ? (
          <span className={cn("rounded px-1.5 py-0.5 text-[10px]", STATUS_CLS[planStatusClass(pct, planSum)])}>
            {pct}%
          </span>
        ) : (
          "—"
        )}
      </div>
    </div>
  );
}
